// 2026-02 · Componente reutilizable de autocompletar de alimentos.
// - Muestra los alimentos seleccionados como chips con "x".
// - Sugiere hasta 8 opciones del catálogo mientras el usuario escribe.
// - Enter agrega la primera sugerencia (o el texto tal cual si no hay match).
// - Backspace en el input vacío borra el último chip.
// - Permite pegar múltiples separados por coma.
// Se usa en ClientProfileModal (admin) y PersonalizedPlanForm (usuaria).
import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { FaTimes } from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL;

// Cache local del catálogo para evitar múltiples fetches por sesión.
let _catalogCache = null;
let _catalogPromise = null;

async function _fetchCatalog() {
  if (_catalogCache) return _catalogCache;
  if (_catalogPromise) return _catalogPromise;
  _catalogPromise = (async () => {
    try {
      // Endpoint público: no requiere token de admin.
      const { data } = await axios.get(`${API}/api/admin/offline-generator/food-database`);
      const flat = [];
      if (data && typeof data === 'object') {
        Object.entries(data).forEach(([cat, foods]) => {
          (foods || []).forEach(f => flat.push({ name: f.name, category: cat }));
        });
      }
      _catalogCache = flat;
      return flat;
    } catch {
      _catalogCache = [];
      return [];
    } finally {
      _catalogPromise = null;
    }
  })();
  return _catalogPromise;
}

export default function FoodAutocompleteField({
  value,
  onChange,
  catalog: providedCatalog,
  placeholder,
  testIdSuffix = 'food',
  chipColorClass = 'bg-pink-500/20 text-pink-200 border-pink-500/40',
  hint,
}) {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [catalog, setCatalog] = useState(providedCatalog || _catalogCache || []);
  const items = Array.isArray(value) ? value : (value ? [String(value)] : []);
  const suppressBlur = useRef(false);

  useEffect(() => {
    if (providedCatalog && providedCatalog.length > 0) {
      setCatalog(providedCatalog);
      return;
    }
    let alive = true;
    _fetchCatalog().then((c) => { if (alive) setCatalog(c); });
    return () => { alive = false; };
  }, [providedCatalog]);

  const suggestions = useMemo(() => {
    // 2026-02 · Normalizador accent-insensitive: elimina tildes y mayúsculas
    // para que "salmon" matchee "Salmón" / "salmón" / "SALMON".
    const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const q = norm(query.trim());
    if (!q || q.length < 2 || !catalog || catalog.length === 0) return [];
    const already = new Set(items.map(i => norm(i)));
    return catalog
      .filter(f => norm(f.name).includes(q) && !already.has(norm(f.name)))
      .slice(0, 8);
  }, [query, catalog, items]);

  const addItem = (name) => {
    const clean = String(name || '').replace(/\s+/g, ' ').trim();
    if (!clean) return;
    const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (items.some(i => norm(i) === norm(clean))) {
      setQuery(''); setShowSuggestions(false);
      return;
    }
    onChange([...items, clean]);
    setQuery(''); setShowSuggestions(false);
  };
  const removeItem = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  const handleKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) addItem(suggestions[0].name);
      else if (query.includes(',')) {
        const parts = query.split(',').map(s => s.replace(/\s+/g, ' ').trim()).filter(Boolean);
        if (parts.length > 0) {
          const merged = [...items];
          parts.forEach(p => { if (!merged.some(x => x.toLowerCase() === p.toLowerCase())) merged.push(p); });
          onChange(merged);
          setQuery(''); setShowSuggestions(false);
        }
      } else if (query.trim()) {
        addItem(query);
      }
    } else if (e.key === 'Backspace' && !query && items.length > 0) {
      removeItem(items.length - 1);
    }
  };

  return (
    <div className="space-y-2" data-testid={`food-autocomplete-${testIdSuffix}`}>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <span
              key={i}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs ${chipColorClass}`}
              data-testid={`food-chip-${testIdSuffix}-${i}`}
            >
              {it}
              <button
                type="button"
                onClick={() => removeItem(i)}
                className="ml-0.5 opacity-75 hover:opacity-100"
                data-testid={`food-chip-remove-${testIdSuffix}-${i}`}
              >
                <FaTimes className="text-[10px]" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => { if (!suppressBlur.current) setShowSuggestions(false); }, 150)}
          onKeyDown={handleKey}
          placeholder={placeholder || 'Escribe un alimento y presiona Enter'}
          className="w-full bg-gray-900/60 border border-gray-700 rounded p-2 text-sm text-white placeholder:text-gray-500"
          data-testid={`food-autocomplete-input-${testIdSuffix}`}
        />
        {showSuggestions && suggestions.length > 0 && (
          <div
            className="absolute left-0 right-0 top-full mt-1 z-20 bg-gray-950 border border-pink-500/40 rounded-lg shadow-lg max-h-52 overflow-y-auto"
            data-testid={`food-suggestions-${testIdSuffix}`}
            onMouseDown={() => { suppressBlur.current = true; }}
            onMouseUp={() => { suppressBlur.current = false; }}
          >
            {suggestions.map((s, si) => (
              <button
                key={si}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); addItem(s.name); }}
                className="w-full text-left px-3 py-2 hover:bg-pink-500/15 text-sm text-white flex justify-between items-center"
                data-testid={`food-suggestion-${testIdSuffix}-${si}`}
              >
                <span>{s.name}</span>
                <span className="text-[10px] text-gray-400">{s.category}</span>
              </button>
            ))}
          </div>
        )}
        <p className="text-[10px] text-gray-500 mt-1">
          {hint || 'Enter para agregar · Backspace en vacío para borrar el último · Coma para pegar varios'}
        </p>
      </div>
    </div>
  );
}
